"use client";

import { useMemo } from "react";
import { parseAsString, useQueryState } from "nuqs";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { ProjectBasemap } from "@/types";

/** The tenant's basemaps, in the order they should appear in the selector. */
export function useBasemaps(): ProjectBasemap[] {
  const { basemaps } = useProjectConfigs();

  return useMemo(() => {
    if (!basemaps?.length) return [];
    // `order` is authored per basemap; fall back to config order where unset so a
    // partially ordered list stays stable instead of reshuffling.
    return [...basemaps].sort(
      (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
    );
  }, [basemaps]);
}

/**
 * The active basemap, backed by the `basemap` query param.
 *
 * Falls back to the one the tenant marked default, then to the first — the map
 * cannot be constructed without a style URL, so "no basemap" must not be a
 * reachable state for a project that defines any.
 */
export function useSelectedBasemap() {
  const [id, setId] = useQueryState("basemap", parseAsString);
  const basemaps = useBasemaps();

  const basemap = useMemo(
    () =>
      basemaps.find((candidate) => candidate.id === id) ??
      basemaps.find((candidate) => candidate.default) ??
      basemaps[0],
    [basemaps, id]
  );

  return { basemap, basemaps, setBasemap: setId };
}
